/**
 * API client for Orgo service
 */

import fetch from 'cross-fetch';
import { getApiKey } from '../utils/auth';
import { ApiError } from '../errors';
import type {
  ProjectInfo,
  ComputerInfo,
  ApiResponse,
  ScreenshotResponse,
  BashResponse,
  ExecResponse,
  StreamStartResponse,
  StreamStatusResponse,
  StreamStopResponse
} from '../types';

export class ApiClient {
  private apiKey: string;
  private baseUrl: string;
  private headers: Record<string, string>;

  constructor(apiKey?: string, baseUrl?: string) {
    this.apiKey = getApiKey(apiKey);
    this.baseUrl = baseUrl || 'https://www.orgo.ai/api';
    this.headers = {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  private async request<T = unknown>(
    method: string,
    endpoint: string,
    data?: unknown
  ): Promise<T> {
    const url = `${this.baseUrl}/${endpoint}`;

    try {
      const options: RequestInit = {
        method,
        headers: this.headers,
      };

      if (method === 'GET' && data) {
        const params = new URLSearchParams(data as Record<string, string>);
        const urlWithParams = `${url}?${params}`;
        const response = await fetch(urlWithParams, options);
        return this.handleResponse<T>(response);
      } else if (data) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);
      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(`Connection error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `API error: ${response.status}`;
      try {
        const errorData = await response.json() as ApiResponse;
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // Ignore JSON parse errors
      }
      throw new ApiError(errorMessage, response.status, response);
    }

    return response.json() as Promise<T>;
  }

  // Project methods
  public async createProject(name: string): Promise<ProjectInfo> {
    return this.request<ProjectInfo>('POST', 'projects', { name });
  }

  public async getProjectByName(name: string): Promise<ProjectInfo> {
    const projects = await this.listProjects();
    const project = projects.find(p => p.name === name);
    if (!project) {
      throw new ApiError(`Project '${name}' not found`, 404);
    }
    return project;
  }

  public async getProject(projectId: string): Promise<ProjectInfo> {
    return this.request<ProjectInfo>('GET', `projects/${projectId}`);
  }

  public async listProjects(): Promise<ProjectInfo[]> {
    const response = await this.request<{ projects: ProjectInfo[] }>('GET', 'projects');
    return response.projects || [];
  }

  public async deleteProject(projectId: string): Promise<unknown> {
    return this.request('DELETE', `projects/${projectId}`);
  }

  // Computer methods
  public async createComputer(
    projectId: string,
    computerName: string,
    os: 'linux' | 'windows' = 'linux',
    ram: number = 2,
    cpu: number = 2,
    gpu: string = 'none'
  ): Promise<ComputerInfo> {
    return this.request<ComputerInfo>('POST', 'computers', {
      project_id: projectId,
      name: computerName,
      os,
      ram,
      cpu,
      gpu
    });
  }

  public async listComputers(projectId: string): Promise<ComputerInfo[]> {
    const project = await this.getProject(projectId);
    return (project as any).desktops || [];
  }

  public async getComputer(computerId: string): Promise<ComputerInfo> {
    return this.request<ComputerInfo>('GET', `computers/${computerId}`);
  }

  public async deleteComputer(computerId: string): Promise<unknown> {
    return this.request('DELETE', `computers/${computerId}`);
  }

  public async restartComputer(computerId: string): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/restart`);
  }

  // Computer control methods
  public async leftClick(computerId: string, x: number, y: number): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/click`, {
      button: 'left',
      x,
      y
    });
  }

  public async rightClick(computerId: string, x: number, y: number): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/click`, {
      button: 'right',
      x,
      y
    });
  }

  public async doubleClick(computerId: string, x: number, y: number): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/click`, {
      button: 'left',
      x,
      y,
      double: true
    });
  }

  public async drag(
    computerId: string,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    button: 'left' | 'right' = 'left',
    duration: number = 0.5
  ): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/drag`, {
      start_x: startX,
      start_y: startY,
      end_x: endX,
      end_y: endY,
      button,
      duration
    });
  }

  public async scroll(computerId: string, direction: string, amount: number = 3): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/scroll`, {
      direction,
      amount
    });
  }

  public async typeText(computerId: string, text: string): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/type`, {
      text
    });
  }

  public async keyPress(computerId: string, key: string): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/key`, {
      key
    });
  }

  public async getScreenshot(computerId: string): Promise<ScreenshotResponse> {
    return this.request<ScreenshotResponse>('GET', `computers/${computerId}/screenshot`);
  }

  public async executeBash(computerId: string, command: string): Promise<BashResponse> {
    return this.request<BashResponse>('POST', `computers/${computerId}/bash`, {
      command
    });
  }

  public async executePython(computerId: string, code: string, timeout: number = 10): Promise<ExecResponse> {
    return this.request<ExecResponse>('POST', `computers/${computerId}/exec`, {
      code,
      timeout
    });
  }

  public async wait(computerId: string, seconds: number): Promise<unknown> {
    return this.request('POST', `computers/${computerId}/wait`, {
      duration: seconds
    });
  }

  // Streaming methods
  public async startStream(computerId: string, connectionName: string): Promise<StreamStartResponse> {
    return this.request<StreamStartResponse>('POST', `computers/${computerId}/stream/start`, {
      connection_name: connectionName
    });
  }

  public async stopStream(computerId: string): Promise<StreamStopResponse> {
    return this.request<StreamStopResponse>('POST', `computers/${computerId}/stream/stop`);
  }

  public async getStreamStatus(computerId: string): Promise<StreamStatusResponse> {
    return this.request<StreamStatusResponse>('GET', `computers/${computerId}/stream/status`);
  }
}